<template>
    <div class="armory-stacked-tabs" :class="{ collapsed: collapsed }">
        <div v-for="tab in tabs" :key="tab.value">
            <div
                class="tab display-flex align-items-center py-sm cursor-pointer px-sm"
                :class="{ active: isTabActive(tab) }"
                @click="onTabClick(tab)"
            >
                <div class="tab-icon-container display-flex justify-center mr-md">
                    <font-awesome-icon
                        v-if="tab.icon"
                        v-tooltip="{
                            content: tab.label,
                            delay: { show: 500, hide: 100 },
                            placement: 'left',
                            distance: 20,
                        }"
                        class="tab-icon text-stone-600"
                        :icon="tab.icon"
                        size="lg"
                    />
                </div>
                <p class="tab-label text-stone-600">{{ tab.label }}</p>
                <div v-if="tab.pro" class="pro-badge">
                    <font-awesome-icon class="tab-icon text-gold" :icon="`fa-crown`" size="sm" />
                </div>
            </div>
            <!-- SubTabs -->
            <div v-if="tab.subTabs && tab.subTabs.length > 0" class="sub-tabs pl-sm mt-sm">
                <div
                    v-for="subTab in tab.subTabs"
                    :key="subTab.value"
                    class="tab sub-tab display-flex align-items-center py-sm cursor-pointer px-sm"
                    :class="{ active: isSubTabActive(subTab) }"
                    @click="onTabClick(tab, subTab)"
                >
                    <div class="tab-icon-container display-flex justify-center mr-md">
                        <font-awesome-icon
                            v-if="subTab.icon"
                            v-tooltip="{
                                content: tab.label + ' - ' + subTab.label,
                                delay: { show: 500, hide: 100 },
                                placement: 'left',
                                distance: 20,
                            }"
                            class="tab-icon text-stone-600"
                            :icon="subTab.icon"
                            size="lg"
                        />
                    </div>
                    <p class="tab-label text-stone-600">{{ subTab.label }}</p>
                    <div v-if="subTab.pro" class="pro-badge">
                        <font-awesome-icon
                            class="tab-icon text-gold"
                            :icon="`fa-crown`"
                            size="sm"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue'

const emit = defineEmits(['tab-change'])
const props = defineProps({
    tabs: {
        type: Array,
        default: () => [
            {
                label: 'Tab 1',
                value: 'tab1',
                icon: 'fa-chart-line',
            },
            {
                label: 'Tab 2',
                value: 'tab2',
                icon: '',
            },
        ],
    },
    currentTab: {
        type: [Number, String],
        default: '',
    },
    currentSubTab: {
        type: [Number, String],
        default: '',
    },
    collapsed: {
        type: Boolean,
        default: false,
    },
})

const isTabActive = (tab) => {
    if (tab.subTabs) {
        return props.currentTab === tab.value
    }
    return props.currentTab === tab.value
}

const isSubTabActive = (subTab) => {
    return props.currentSubTab === subTab.value
}

const onTabClick = (tab, subTab) => {
    if (tab.onClick) {
        tab.onClick()
    } else {
        emit('tab-change', tab, subTab)
    }
}
</script>

<style lang="scss" scoped>
.armory-stacked-tabs {
    display: flex;
    flex-direction: column;
    gap: 10px;
    transition: all 0.3s ease-in-out;

    &.collapsed {
        .tab {
            padding-left: 12px;
            padding-right: 12px;

            &.active {
                background-color: var(--color-stone-0) !important;
            }

            &:hover {
                background-color: var(--color-stone-0) !important;
            }
        }

        .tab-label {
            opacity: 0;
            width: 0;
            margin-left: 0;
            transition: all 0.3s ease-in-out;
        }

        .tab-icon-container {
            margin-right: 0;
            transition: all 0.3s ease-in-out;
        }

        .pro-badge {
            opacity: 0;
            transition: all 0.3s ease-in-out;
        }

        .sub-tabs {
            border-color: transparent;
            transform: translateX(-10px);
            margin: 0px !important;
            margin-left: -1px !important;
        }
    }
}

.tab-icon-container {
    width: 20px;
    height: 20px;
    transition: all 0.3s ease-in-out;
}

.tab {
    position: relative;
    border-radius: var(--border-radius-md);
    transition: all 0.3s ease-in-out;

    .tab-label {
        transition: all 0.3s ease-in-out;
        white-space: nowrap;
        overflow: hidden;
    }

    .tab-icon {
        max-width: 20px;
        max-height: 20px;
    }

    p,
    .tab-icon {
        transition: all 0.3s ease-in-out;
    }

    &:hover {
        background-color: var(--color-stone-300);
        p {
            color: var(--color-stone-600) !important;
        }

        .tab-icon {
            color: var(--color-moat-500) !important;
        }

        .pro-badge {
            .tab-icon {
                color: var(--color-gold) !important;
            }
        }
    }

    &.active {
        background-color: var(--color-stone-300);

        p {
            color: var(--color-stone-700) !important;
        }

        .tab-icon {
            color: var(--color-moat-600) !important;
        }

        .pro-badge {
            .tab-icon {
                color: var(--color-gold) !important;
            }
        }
    }

    .pro-badge {
        position: absolute;
        top: 13px;
        right: 14px;
        opacity: 1;
        transition: all 0.3s ease-in-out;
    }
}

.sub-tabs {
    margin-left: 24px;
    transform: translateX(0);
    border-left: 1px solid var(--color-stone-200);

    transition: all 0.3s ease-in-out;

    .sub-tab {
        font-size: 0.9em;

        &:hover {
            background-color: var(--color-stone-200);
        }

        &.active {
            background-color: var(--color-stone-300);
        }
    }
}
</style>
